/**
 * Persists a post-login navigation target from email deep links (?redirect=...).
 * Only same-origin relative paths under an explicit allowlist are stored (open-redirect safe).
 */

import { jwtDecode } from 'jwt-decode';

/** sessionStorage key for the validated reports deep-link path (email → login → reports). */
export const BRAINCARE_REPORTS_REDIRECT_SESSION_KEY = 'braincareReportsRedirect';

const RESOLVER_BASE = 'http://redirect-sanitizer.invalid';

/** Pathnames that may appear as the login landing target (path + query allowed). */
const ALLOWED_PATHNAMES = ['/braincare/reports', '/organization/physio'];

/**
 * @param {string} raw — typically `redirect` query value (may be URI-encoded once by the mail client).
 * @returns {string | null} — safe relative path starting with `/`, including search/hash; or null.
 */
export function sanitizePostLoginRedirect(raw) {
  if (raw == null || typeof raw !== 'string') {
    return null;
  }

  let decoded = raw.trim();
  if (!decoded) {
    return null;
  }

  try {
    decoded = decodeURIComponent(decoded);
  } catch {
    return null;
  }

  decoded = decoded.trim();
  if (!decoded) {
    return null;
  }

  if (decoded.includes('://') || decoded.includes('\n') || decoded.includes('\r')) {
    return null;
  }

  let resolved;
  try {
    resolved = new URL(decoded, RESOLVER_BASE);
  } catch {
    return null;
  }

  if (resolved.origin !== new URL(RESOLVER_BASE).origin) {
    return null;
  }

  const pathname = resolved.pathname.replace(/\/+$/, '') || '/';
  if (!ALLOWED_PATHNAMES.includes(pathname)) {
    return null;
  }

  return `${pathname}${resolved.search}${resolved.hash}`;
}

/**
 * JWT access payload uses the claim name `org_id`, but the value is the organization's
 * **physio_org_id** (see backend `jwt_data`), not the organization table PK.
 * @param {string | undefined | null} accessToken
 * @returns {string | null}
 */
export function getSessionPhysioOrgIdFromAccessToken(accessToken) {
  if (accessToken == null || String(accessToken).trim() === '') {
    return null;
  }
  try {
    const payload = jwtDecode(accessToken);
    const raw = payload?.org_id;
    if (raw == null || raw === '') {
      return null;
    }
    return String(raw);
  } catch {
    return null;
  }
}

/**
 * @param {string} relativePath — sanitized path with optional query (e.g. `/braincare/reports?org_id=5`).
 * @returns {number | null}
 */
export function parseOrgIdFromRelativePath(relativePath) {
  if (relativePath == null || typeof relativePath !== 'string') {
    return null;
  }
  try {
    const resolved = new URL(relativePath, RESOLVER_BASE);
    const param = resolved.searchParams.get('org_id');
    if (param == null || param === '') {
      return null;
    }
    const n = Number.parseInt(param, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/**
 * Email `org_id` query is the organization PK; JWT `org_id` claim matches that org's `physio_org_id`.
 * Used to skip DirectLogin when Redux `active_organization.id` is still -1 but the session is already
 * on the linked org (typical BC admin shell).
 * @param {string | undefined | null} accessToken
 * @param {string | null} relativePath — sanitized redirect path
 * @param {Array<{ id?: unknown, physio_org_id?: unknown }> | undefined} organizations — Redux `user.organizations`
 * @returns {boolean}
 */
export function isPendingDeepLinkOrgAlignedWithJwtPhysio(accessToken, relativePath, organizations) {
  const pendingPk = parseOrgIdFromRelativePath(relativePath);
  if (pendingPk == null) {
    return false;
  }
  const jwtPhysio = getSessionPhysioOrgIdFromAccessToken(accessToken);
  if (jwtPhysio == null) {
    return false;
  }
  if (!Array.isArray(organizations) || organizations.length === 0) {
    return false;
  }
  const match = organizations.find((o) => String(o.id) === String(pendingPk));
  if (match == null || match.physio_org_id == null || match.physio_org_id === '') {
    return false;
  }
  return String(match.physio_org_id) === String(jwtPhysio);
}

/**
 * Member alert links use `/organization/physio?...`; Braincare admins should land on
 * `/braincare/reports?...` with the same query (same umi/org_id/action contract).
 * @param {string} relativePath — sanitized path (e.g. from `sanitizePostLoginRedirect`).
 * @returns {string}
 */
export function rewriteMemberReportsPathForBraincareAdmin(relativePath) {
  if (relativePath == null || typeof relativePath !== 'string' || !relativePath.startsWith('/')) {
    return relativePath;
  }
  try {
    const resolved = new URL(relativePath, RESOLVER_BASE);
    const pathname = resolved.pathname.replace(/\/+$/, '') || '/';
    if (pathname !== '/organization/physio') {
      return relativePath;
    }
    return `/braincare/reports${resolved.search}${resolved.hash}`;
  } catch {
    return relativePath;
  }
}

/**
 * @param {string} raw
 * @returns {boolean} whether a value was written to sessionStorage
 */
export function persistPostLoginRedirect(raw) {
  const safe = sanitizePostLoginRedirect(raw);
  if (!safe) {
    return false;
  }
  try {
    sessionStorage.setItem(BRAINCARE_REPORTS_REDIRECT_SESSION_KEY, safe);
  } catch {
    return false;
  }
  return true;
}

/**
 * @returns {string | null}
 */
export function getPendingPostLoginRedirect() {
  try {
    const stored = sessionStorage.getItem(BRAINCARE_REPORTS_REDIRECT_SESSION_KEY);
    return sanitizePostLoginRedirect(stored);
  } catch {
    return null;
  }
}

export function clearPendingPostLoginRedirect() {
  try {
    sessionStorage.removeItem(BRAINCARE_REPORTS_REDIRECT_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * If sessionStorage holds a validated deep-link path, navigate there and clear it.
 * Call after full login (tokens set); skip while terms are still pending.
 * @param {(to: string, opts?: { replace?: boolean }) => void} navigate — react-router navigate
 * @param {{ isBraincareUser?: boolean }} [options]
 * @returns {boolean} true if navigation was performed
 */
export function tryNavigateToPendingPostLoginRedirect(navigate, options = {}) {
  let pending = getPendingPostLoginRedirect();
  if (!pending) {
    return false;
  }
  if (options.isBraincareUser) {
    pending = rewriteMemberReportsPathForBraincareAdmin(pending);
  }
  clearPendingPostLoginRedirect();
  navigate(pending, { replace: true });
  return true;
}

/**
 * @param {unknown} activeOrganizationId — current session org (from JWT/profile).
 * @param {string | null} relativePath — pending redirect path (may include `org_id` query).
 * @returns {boolean} false when `org_id` is present and does not match the active org.
 */
export function isOrgAlignedWithPendingRedirect(activeOrganizationId, relativePath) {
  const expected = parseOrgIdFromRelativePath(relativePath);
  if (expected == null) {
    return true;
  }
  if (activeOrganizationId == null || activeOrganizationId === -1) {
    return false;
  }
  const aligned = String(activeOrganizationId) === String(expected);
  return aligned;
}

/** Backend / e-mail: `open` | `filter`; anything else defaults to `open`. */
export function normalizeReportDeepLinkAction(raw) {
  const s = String(raw ?? '').trim().toLowerCase();
  return s === 'filter' ? 'filter' : 'open';
}

/**
 * Remove deep-link query keys after applying them (keeps other search params).
 * @param {(next: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams)) => void} setSearchParams
 */
export function clearReportDeepLinkSearchParams(setSearchParams) {
  setSearchParams(
    (prev) => {
      const next = new URLSearchParams(prev);
      next.delete('umi');
      next.delete('org_id');
      next.delete('action');
      return next;
    },
    { replace: true },
  );
}
